// The two call-processing pipelines an eval can run on a real model.
//
// `runStagedPipeline` is production's 5-stage pipeline, copied from trackable-api
// src/lib/langgraph (pipeline.ts and stages/*.ts): the same prompts, messages, schemas,
// skip rules and fallbacks. The one known difference: no workspace prompt addendum.
//
// `runSinglePipeline` is the v2 candidate: one model call returns every stage's fields,
// then the same skip rules and stage 5 code rules (assemble.ts) produce the same output.

import { assemblePeople, needsActionItems } from './assemble'
import {
  ACTION_ITEMS_SCHEMA,
  APPOINTMENT_SCHEMA,
  OUTCOME_SCHEMA,
  OVERVIEW_SCHEMA,
  PEOPLE_SCHEMA,
  SINGLE_SCHEMA,
} from './schemas'
import { getAppointmentContext } from './transcriptContext'
import type {
  AppointmentData,
  CallType,
  ExtractedPerson,
  FinalOutput,
  ModelCall,
  OutcomeData,
  Overview,
  PipelineInput,
  PipelineResult,
  StageResults,
} from './types'
import type { PipelineStage } from '../types'

type Args = Record<string, unknown>

/** Production formats the call date on servers that run in UTC. */
function callDateText(iso: string): string {
  const date = new Date(iso)
  return new Date(date.getTime() + date.getTimezoneOffset() * 60_000).toDateString()
}

const text = (value: unknown) => (typeof value === `string` && value ? value : undefined)

const MISSED_CALL_OVERVIEW: Overview = { summary: `Missed call - no transcription available`, callType: `missed_call` }
const NULL_OVERVIEW: Overview = { summary: `Unable to extract call summary`, callType: `other` }
const NULL_PEOPLE: ExtractedPerson[] = [{ name: undefined, isCaller: true, patientType: `Other` }]

/** Stages 3's skip: these call types get no appointment data from the model. */
const NO_APPOINTMENT_TYPES: CallType[] = [`missed_call`, `wrong_number`, `interoffice`]

const noAppointments = (people: ExtractedPerson[]): AppointmentData[] =>
  people.map((_, personIndex) => ({ personIndex, appointmentRequested: false, appointmentScheduled: false }))

function toOverview(args: Args): Overview {
  return {
    answerer: text(args.answerer),
    summary: text(args.summary),
    channel: text(args.channel),
    callType: text(args.callType) as CallType | undefined,
  }
}

function toPerson(raw: Args): ExtractedPerson {
  return {
    name: raw.name === `unknown` || !raw.name ? undefined : String(raw.name),
    isCaller: Boolean(raw.isCaller),
    relationship: text(raw.relationship),
    patientType: String(raw.patientType ?? `Other`),
    phone: text(raw.phone),
    email: text(raw.email),
    birthDate: text(raw.birthDate),
  }
}

/** One person's appointment answer; a person the model skipped gets no appointment. */
function toAppointment(personIndex: number, found?: Args): AppointmentData {
  return {
    personIndex,
    appointmentRequested: Boolean(found?.appointmentRequested ?? false),
    appointmentScheduled: Boolean(found?.appointmentScheduled ?? false),
    appointmentDate: text(found?.appointmentDate),
    appointmentTime: text(found?.appointmentTime),
    treatment: text(found?.treatment),
    serviceRequested: text(found?.serviceRequested),
    reasonForNotScheduling: text(found?.reasonForNotScheduling),
  }
}

function toOutcome(personIndex: number, found?: Args): OutcomeData {
  return {
    personIndex,
    outcomes: Array.isArray(found?.outcomes) ? (found?.outcomes as string[]) : [`Other`],
    insuranceProvider: text(found?.insuranceProvider),
    referral: text(found?.referral),
  }
}

/** Voicemails keep their content; the caller also gets "Voicemail". */
function addVoicemail(overview: Overview | undefined, people: ExtractedPerson[], outcomes: OutcomeData[]): void {
  if (overview?.callType !== `voicemail`) return
  const caller = outcomes.find((entry) => entry.personIndex === people.findIndex((p) => p.isCaller))
  if (caller && !caller.outcomes.includes(`Voicemail`)) caller.outcomes.push(`Voicemail`)
}

function finalOutput(results: StageResults, actionItems?: string): FinalOutput {
  return {
    people: assemblePeople(results),
    summary: results.overview?.summary,
    actionItems,
    answerer: results.overview?.answerer,
    channel: results.overview?.channel,
  }
}

export async function runStagedPipeline(
  input: PipelineInput,
  prompts: Record<PipelineStage, string>,
  call: ModelCall,
): Promise<PipelineResult> {
  const usage = { promptTokens: 0, completionTokens: 0 }
  let calls = 0
  const ask = async (system: string, user: string, parameters: Args) => {
    const response = await call({ system, user, parameters })
    usage.promptTokens += response.usage.promptTokens
    usage.completionTokens += response.usage.completionTokens
    calls++
    return response.args
  }
  const date = callDateText(input.callDate)
  const transcript = input.transcript

  // Stage 1: overview.
  if (!transcript.trim()) {
    return { output: finalOutput({ overview: MISSED_CALL_OVERVIEW }), callType: `missed_call`, usage, calls }
  }
  const overviewArgs = await ask(
    prompts.overview,
    `Call date: ${date}\n\nTranscription:\n${transcript}`,
    OVERVIEW_SCHEMA,
  )
  const overview = overviewArgs ? toOverview(overviewArgs) : NULL_OVERVIEW
  // A missed call skips straight to stage 5, with no people.
  if (overview.callType === `missed_call`) {
    return { output: finalOutput({ overview }), callType: overview.callType, usage, calls }
  }

  // Stage 2: people.
  const peopleArgs = await ask(prompts.people, `Call date: ${date}\n\nTranscription:\n${transcript}`, PEOPLE_SCHEMA)
  const rawPeople = peopleArgs?.people as Args[] | undefined
  const people = rawPeople ? rawPeople.map(toPerson) : NULL_PEOPLE
  const peopleContext = people
    .map((p, i) => `Person ${i}: ${p.name || `unknown`} (${p.patientType}${p.isCaller ? `, caller` : ``})`)
    .join(`\n`)

  // Stage 3: appointments.
  let appointmentData = noAppointments(people)
  if (people.length && !NO_APPOINTMENT_TYPES.includes(overview.callType as CallType)) {
    const args = await ask(
      prompts.appointment,
      `Call date: ${date}
Call summary: ${overview.summary || `N/A`}

People identified:
${peopleContext}

Transcription (appointment-relevant segments):
${getAppointmentContext(transcript)}`,
      APPOINTMENT_SCHEMA,
    )
    const found = args?.appointments as Args[] | undefined
    if (found)
      appointmentData = people.map((_, i) =>
        toAppointment(
          i,
          found.find((a) => a.personIndex === i),
        ),
      )
  }

  // Stage 4: outcomes.
  let outcomeData: OutcomeData[] = []
  if (people.length) {
    const context = people
      .map((p, i) => {
        const appt = appointmentData.find((a) => a.personIndex === i)
        return `Person ${i}: ${p.name || `unknown`} (${p.patientType}${p.isCaller ? `, caller` : ``})
  - Appointment requested: ${appt?.appointmentRequested ?? `unknown`}
  - Appointment scheduled: ${appt?.appointmentScheduled ?? `unknown`}${
    appt?.reasonForNotScheduling ? `\n  - Reason not scheduled: ${appt.reasonForNotScheduling}` : ``
  }`
      })
      .join(`\n\n`)
    const args = await ask(
      prompts.outcome,
      `Call date: ${date}
Call summary: ${overview.summary || `N/A`}
Call type: ${overview.callType || `unknown`}
Channel: ${overview.channel || `unknown`}

People and appointment data:
${context}

Transcription (beginning and end):
${transcript}`,
      OUTCOME_SCHEMA,
    )
    const found = args?.outcomes as Args[] | undefined
    outcomeData = people.map((_, i) =>
      toOutcome(
        i,
        found?.find((o) => o.personIndex === i),
      ),
    )
    // Production adds "Voicemail" only after a usable answer, not to the null fallback.
    if (found) addVoicemail(overview, people, outcomeData)
  }

  // Stage 5: code rules, then action items only when an outcome needs follow-up.
  const results: StageResults = { overview, people, appointmentData, outcomeData }
  const assembled = assemblePeople(results)
  let actionItems: string | undefined
  if (needsActionItems(assembled)) {
    const args = await ask(
      prompts.validation,
      `Call summary: ${overview.summary || `N/A`}
Outcomes: ${assembled.flatMap((p) => p.outcomes).join(`, `)}
People: ${assembled.map((p) => `${p.name || `unknown`} (${p.patientType})`).join(`, `)}`,
      ACTION_ITEMS_SCHEMA,
    )
    actionItems = text(args?.actionItems)
  }
  return { output: finalOutput(results, actionItems), callType: overview.callType, usage, calls }
}

export async function runSinglePipeline(
  input: PipelineInput,
  prompt: string,
  call: ModelCall,
): Promise<PipelineResult> {
  if (!input.transcript.trim()) {
    return {
      output: finalOutput({ overview: MISSED_CALL_OVERVIEW }),
      callType: `missed_call`,
      usage: { promptTokens: 0, completionTokens: 0 },
      calls: 0,
    }
  }
  const { args, usage } = await call({
    system: prompt,
    user: `Call date: ${callDateText(input.callDate)}\n\nTranscription:\n${input.transcript}`,
    parameters: SINGLE_SCHEMA,
  })

  // A null answer falls back as every stage would: an unknown caller with "Other".
  const overview = args ? toOverview(args) : NULL_OVERVIEW
  if (overview.callType === `missed_call`)
    return { output: finalOutput({ overview }), callType: `missed_call`, usage, calls: 1 }

  const rawPeople = args?.people as Args[] | undefined
  const people = rawPeople?.length ? rawPeople.map(toPerson) : NULL_PEOPLE
  // The same skip as stage 3: these call types get no appointment data.
  const appointmentData = NO_APPOINTMENT_TYPES.includes(overview.callType as CallType)
    ? noAppointments(people)
    : people.map((_, i) => toAppointment(i, rawPeople?.[i]))
  const outcomeData = people.map((_, i) => toOutcome(i, rawPeople?.[i]))
  addVoicemail(overview, people, outcomeData)

  const results: StageResults = { overview, people, appointmentData, outcomeData }
  const actionItems = needsActionItems(assemblePeople(results)) ? text(args?.actionItems) : undefined
  return { output: finalOutput(results, actionItems), callType: overview.callType, usage, calls: 1 }
}
